import React, { useState, useEffect, useRef } from 'react';
import axios from 'axios';
import { Button } from './components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from './components/ui/card';
import { Input } from './components/ui/input';
import { Badge } from './components/ui/badge';
import { toast, Toaster } from 'sonner';
import { 
  Users, 
  MapPin, 
  Calendar, 
  TrendingUp, 
  MessageCircle,
  Bot,
  User,
  Send,
  X,
  BarChart3,
  Activity,
  Clock,
  Eye,
  Trash2,
  Zap,
  Shield,
  History,
  Plus
} from 'lucide-react';
import TymeLoader from './components/TymeLoader';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const SuperAdminDashboard = () => {
  const [isLoggedIn, setIsLoggedIn] = useState(false);
  const [sessionId, setSessionId] = useState('');
  const [loginForm, setLoginForm] = useState({ username: '', password: '' });
  const [isLoading, setIsLoading] = useState(false);
  const [analytics, setAnalytics] = useState(null);
  const [lastUpdated, setLastUpdated] = useState(null);
  
  // User Management States
  const [users, setUsers] = useState([]);
  const [usersLoading, setUsersLoading] = useState(false);
  const [searchQuery, setSearchQuery] = useState('');
  const [planFilter, setPlanFilter] = useState('all');
  const [selectedUser, setSelectedUser] = useState(null);
  const [showPlanChangeModal, setShowPlanChangeModal] = useState(false);
  const [planChangeData, setPlanChangeData] = useState({ plan: '', isVip: false, reason: '' });
  const [activeTab, setActiveTab] = useState('overview'); // 'overview' or 'users' or 'vip_backups' or 'automated_backups'

  // VIP Backup States
  const [backups, setBackups] = useState([]);
  const [backupsLoading, setBackupsLoading] = useState(false);
  const [latestBackup, setLatestBackup] = useState(null);
  const [showRestoreConfirm, setShowRestoreConfirm] = useState(false);
  const [selectedBackup, setSelectedBackup] = useState(null);

  // Automated Backup States
  const [automatedBackupStatus, setAutomatedBackupStatus] = useState(null);
  const [userBackups, setUserBackups] = useState([]);
  const [automatedBackupsLoading, setAutomatedBackupsLoading] = useState(false);

  // Jarvis AI Assistant State
  const [showJarvis, setShowJarvis] = useState(false);
  const [jarvisMessages, setJarvisMessages] = useState([
    {
      type: 'bot',
      content: 'Hello! I\'m Jarvis, your AI analytics assistant. I can help you understand platform usage, user behavior, and provide business insights based on real-time data. What would you like to know?'
    }
  ]);
  const [jarvisInput, setJarvisInput] = useState('');
  const [jarvisLoading, setJarvisLoading] = useState(false);
  const [chatHistory, setChatHistory] = useState([]);
  const [currentChatId, setCurrentChatId] = useState(null);
  const [showChatHistory, setShowChatHistory] = useState(false);
  const messagesEndRef = useRef(null);

  // AI Admin Assistant State (New Feature)
  const [aiMessages, setAiMessages] = useState([]);
  const [aiInput, setAiInput] = useState('');
  const [aiLoading, setAiLoading] = useState(false);
  const [selectedFiles, setSelectedFiles] = useState([]);
  const aiMessagesEndRef = useRef(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: "smooth" });
  };

  useEffect(() => {
    scrollToBottom();
  }, [jarvisMessages]);

  // Check for existing super admin session
  useEffect(() => {
    const savedSession = localStorage.getItem('super_admin_session');
    if (savedSession) {
      setSessionId(savedSession);
      setIsLoggedIn(true);
      loadAnalytics(savedSession);
      loadChatHistory();
    }
  }, []);

  // Load chat history from localStorage
  const loadChatHistory = () => {
    const savedHistory = localStorage.getItem('jarvis_chat_history');
    if (savedHistory) {
      setChatHistory(JSON.parse(savedHistory));
    }
  };

  // Save chat history to localStorage
  const saveChatHistory = (history) => {
    localStorage.setItem('jarvis_chat_history', JSON.stringify(history));
  };

  // Create new chat session
  const createNewChat = () => {
    const newChatId = Date.now().toString();
    const newChat = {
      id: newChatId,
      title: 'New Chat',
      messages: [
        {
          type: 'bot',
          content: 'Hello! I\'m Jarvis, your AI analytics assistant. I can help you understand platform usage, user behavior, and provide business insights based on real-time data. What would you like to know?'
        }
      ],
      createdAt: new Date().toISOString(),
      lastActivity: new Date().toISOString()
    };
    
    const updatedHistory = [newChat, ...chatHistory];
    setChatHistory(updatedHistory);
    saveChatHistory(updatedHistory);
    setCurrentChatId(newChatId);
    setJarvisMessages(newChat.messages);
  };

  // Load existing chat
  const loadChat = (chatId) => {
    const chat = chatHistory.find(c => c.id === chatId);
    if (chat) {
      setCurrentChatId(chatId);
      setJarvisMessages(chat.messages);
    }
  };

  // Delete chat
  const deleteChat = (chatId) => {
    const updatedHistory = chatHistory.filter(c => c.id !== chatId);
    setChatHistory(updatedHistory);
    saveChatHistory(updatedHistory);
    
    if (currentChatId === chatId) {
      if (updatedHistory.length > 0) {
        loadChat(updatedHistory[0].id);
      } else {
        createNewChat();
      }
    }
  };

  // Update current chat in history
  const updateCurrentChatInHistory = (messages) => {
    if (!currentChatId) return;
    
    const updatedHistory = chatHistory.map(chat => {
      if (chat.id === currentChatId) {
        return {
          ...chat,
          messages,
          lastActivity: new Date().toISOString(),
          title: messages[1]?.content?.substring(0, 50) + '...' || 'New Chat'
        };
      }
      return chat;
    });
    
    setChatHistory(updatedHistory);
    saveChatHistory(updatedHistory);
  };

  // Auto-refresh analytics every 30 seconds
  useEffect(() => {
    if (isLoggedIn && sessionId) {
      const interval = setInterval(() => {
        loadAnalytics(sessionId);
      }, 30000);
      return () => clearInterval(interval);
    }
  }, [isLoggedIn, sessionId]);

  const handleLogin = async (e) => {
    e.preventDefault();
    setIsLoading(true);

    try {
      const response = await axios.post(`${API}/super-admin/login`, loginForm);
      setSessionId(response.data.session_id);
      setIsLoggedIn(true);
      
      // Save session
      localStorage.setItem('super_admin_session', response.data.session_id);
      
      // Load initial analytics
      await loadAnalytics(response.data.session_id);
      
      toast.success('Welcome to Super Admin Dashboard');
    } catch (error) {
      toast.error('Invalid super admin credentials');
    } finally {
      setIsLoading(false);
    }
  };

  const loadAnalytics = async (sessionId) => {
    try {
      const response = await axios.get(`${API}/super-admin/analytics?session_id=${sessionId}`);
      setAnalytics(response.data);
      setLastUpdated(new Date());
    } catch (error) {
      console.error('Error loading analytics:', error);
      if (error.response?.status === 401) {
        // Session expired - logout silently
        setIsLoggedIn(false);
        setSessionId('');
        setAnalytics(null);
        localStorage.removeItem('super_admin_session');
      }
    }
  };

  const handleLogout = () => {
    setIsLoggedIn(false);
    setSessionId('');
    setAnalytics(null);
    localStorage.removeItem('super_admin_session');
    toast.success('Logged out successfully');
  };

  const sendJarvisMessage = async () => {
    if (!jarvisInput.trim() || jarvisLoading) return;

    const userMessage = jarvisInput.trim();
    setJarvisInput('');
    
    // Add user message
    const newMessages = [...jarvisMessages, { type: 'user', content: userMessage }];
    setJarvisMessages(newMessages);
    setJarvisLoading(true);

    // If no current chat, create one
    if (!currentChatId) {
      createNewChat();
    }

    try {
      const response = await axios.post(`${API}/super-admin/jarvis`, {
        message: userMessage
      }, {
        params: { session_id: sessionId }
      });

      // Add bot response
      const finalMessages = [...newMessages, { 
        type: 'bot', 
        content: response.data.response 
      }];
      
      setJarvisMessages(finalMessages);
      updateCurrentChatInHistory(finalMessages);
      
    } catch (error) {
      console.error('Jarvis error:', error);
      const errorMessages = [...newMessages, { 
        type: 'bot', 
        content: 'I apologize, but I\'m experiencing technical difficulties. Please try again in a moment.' 
      }];
      
      setJarvisMessages(errorMessages);
      updateCurrentChatInHistory(errorMessages);
    } finally {
      setJarvisLoading(false);
    }
  };

  const handleJarvisKeyPress = (e) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      sendJarvisMessage();
    }
  };

  // AI Admin Assistant Functions
  const loadAiChatHistory = async () => {
    try {
      const response = await axios.get(`${API}/super-admin/ai-assistant/history`, {
        params: { session_id: sessionId }
      });
      
      if (response.data.success && response.data.messages.length > 0) {
        const formattedMessages = response.data.messages.flatMap(msg => [
          { 
            type: 'user', 
            content: msg.user_message,
            timestamp: msg.timestamp,
            files: msg.files?.map(f => f.filename) || []
          },
          { 
            type: 'bot', 
            content: msg.ai_response,
            timestamp: msg.timestamp 
          }
        ]);
        setAiMessages(formattedMessages);
      }
    } catch (error) {
      console.error('Error loading AI chat history:', error);
    }
  };

  const handleAiSendMessage = async () => {
    if ((!aiInput.trim() && selectedFiles.length === 0) || aiLoading) return;

    const userMessage = aiInput.trim();
    const files = [...selectedFiles];
    
    setAiInput('');
    setAiLoading(true);

    // Add user message to chat
    const newUserMsg = {
      type: 'user',
      content: userMessage || 'Analyzing uploaded files...',
      timestamp: new Date().toISOString(),
      files: files.map(f => f.name)
    };
    setAiMessages(prev => [...prev, newUserMsg]);

    try {
      const formData = new FormData();
      formData.append('message', userMessage || 'Please analyze these files and provide insights.');
      
      // Append files
      files.forEach(file => {
        formData.append('files', file);
      });

      const response = await axios.post(
        `${API}/super-admin/ai-assistant/chat?session_id=${sessionId}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data'
          }
        }
      );

      if (response.data.success) {
        const botMsg = {
          type: 'bot',
          content: response.data.message,
          timestamp: new Date().toISOString()
        };
        setAiMessages(prev => [...prev, botMsg]);
        setSelectedFiles([]);
        
        if (response.data.files_processed > 0) {
          toast.success(`Processed ${response.data.files_processed} file(s) successfully`);
        }
      }
    } catch (error) {
      console.error('AI Assistant error:', error);
      const errorMsg = {
        type: 'bot',
        content: 'I apologize, but I encountered an error processing your request. Please try again.',
        timestamp: new Date().toISOString()
      };
      setAiMessages(prev => [...prev, errorMsg]);
      toast.error('Failed to communicate with AI assistant');
    } finally {
      setAiLoading(false);
    }
  };

  const handleClearAiChat = async () => {
    if (!window.confirm('Are you sure you want to clear all AI chat history?')) return;

    try {
      await axios.delete(`${API}/super-admin/ai-assistant/history`, {
        params: { session_id: sessionId }
      });
      setAiMessages([]);
      toast.success('Chat history cleared');
    } catch (error) {
      console.error('Error clearing chat:', error);
      toast.error('Failed to clear chat history');
    }
  };

  // Load AI chat history when tab is opened
  useEffect(() => {
    if (activeTab === 'ai_assistant' && sessionId && aiMessages.length === 0) {
      loadAiChatHistory();
    }
  }, [activeTab, sessionId]);

  // Load Users
  const loadUsers = async () => {
    try {
      setUsersLoading(true);
      const response = await axios.get(
        `${API}/super-admin/users?session_id=${sessionId}&search=${searchQuery}&plan_filter=${planFilter}`
      );
      setUsers(response.data.users || []);
    } catch (error) {
      console.error('Error loading users:', error);
      toast.error('Failed to load users');
    } finally {
      setUsersLoading(false);
    }
  };

  // Handle Plan Change
  const handlePlanChange = async () => {
    if (!selectedUser || !planChangeData.plan) return;
    
    setUsersLoading(true);
    try {
      await axios.put(
        `${API}/super-admin/user/${selectedUser.id}/plan?session_id=${sessionId}`,
        {
          plan: planChangeData.plan,
          is_vip: planChangeData.isVip,
          reason: planChangeData.reason || 'Manual admin update'
        }
      );
      
      toast.success(`User plan updated to ${planChangeData.plan}`);
      setShowPlanChangeModal(false);
      setPlanChangeData({ plan: '', isVip: false, reason: '' });
      // Immediate reload to reflect changes
      await loadUsers();
    } catch (error) {
      console.error('Error updating user plan:', error);
      toast.error('Failed to update user plan');
    } finally {
      setUsersLoading(false);
    }
  };

  // Handle Suspend User
  const handleSuspendUser = async (userId) => {
    if (!confirm('Are you sure you want to suspend this user?')) return;
    
    setUsersLoading(true);
    try {
      await axios.put(
        `${API}/super-admin/user/${userId}/status?session_id=${sessionId}`,
        { action: 'suspend', reason: 'Admin action' }
      );
      
      toast.success('User suspended successfully');
      // Immediate reload to reflect changes
      await loadUsers();
    } catch (error) {
      console.error('Error suspending user:', error);
      toast.error('Failed to suspend user');
    } finally {
      setUsersLoading(false);
    }
  };

  // Handle Delete User
  const handleDeleteUser = async (userId) => {
    if (!confirm('Are you sure you want to delete this user? This action marks the account as deleted but preserves data.')) return;
    
    setUsersLoading(true);
    try {
      await axios.delete(`${API}/super-admin/user/${userId}?session_id=${sessionId}`);
      toast.success('User deleted successfully');
      // Immediate reload to reflect changes
      await loadUsers();
    } catch (error) {
      console.error('Error deleting user:', error);
      toast.error('Failed to delete user');
    } finally {
      setUsersLoading(false);
    }
  };

  // VIP Backup Functions
  const loadBackups = async () => {
    try {
      setBackupsLoading(true);
      const response = await axios.get(`${API}/super-admin/backup/sibel/list?session_id=${sessionId}`);
      setBackups(response.data.backups || []);
    } catch (error) {
      console.error('Error loading backups:', error);
      toast.error('Failed to load backups');
    } finally {
      setBackupsLoading(false);
    }
  };

  const loadLatestBackup = async () => {
    try {
      const response = await axios.get(`${API}/super-admin/backup/sibel/latest?session_id=${sessionId}`);
      if (response.data.has_backup) {
        setLatestBackup(response.data.backup);
      } else {
        setLatestBackup(null);
      }
    } catch (error) {
      console.error('Error loading latest backup:', error);
    }
  };

  const handleCreateBackup = async () => {
    try {
      toast.loading('Creating backup...');
      const response = await axios.post(`${API}/super-admin/backup/sibel/create?session_id=${sessionId}`);
      toast.success('Backup created successfully');
      loadBackups();
      loadLatestBackup();
    } catch (error) {
      console.error('Error creating backup:', error);
      toast.error('Failed to create backup');
    }
  };

  const handleRestoreBackup = async (backupId) => {
    if (!confirm('⚠️ WARNING: This will restore all data from the selected backup and overwrite current data. Are you absolutely sure?')) {
      return;
    }
    
    try {
      toast.loading('Restoring backup...');
      await axios.post(
        `${API}/super-admin/backup/sibel/restore?session_id=${sessionId}`,
        { backup_id: backupId }
      );
      toast.success('Backup restored successfully');
      setShowRestoreConfirm(false);
      setSelectedBackup(null);
    } catch (error) {
      console.error('Error restoring backup:', error);
      toast.error('Failed to restore backup');
    }
  };

  const handleDeleteBackup = async (backupId) => {
    if (!confirm('Are you sure you want to delete this backup? This action cannot be undone.')) return;
    
    try {
      await axios.delete(`${API}/super-admin/backup/sibel/${backupId}?session_id=${sessionId}`);
      toast.success('Backup deleted successfully');
      loadBackups();
      loadLatestBackup();
    } catch (error) {
      console.error('Error deleting backup:', error);
      toast.error('Failed to delete backup');
    }
  };

  // Automated Backup Functions
  const loadAutomatedBackupStatus = async () => {
    try {
      const response = await axios.get(`${API}/super-admin/backup/automated/status?session_id=${sessionId}`);
      setAutomatedBackupStatus(response.data);
    } catch (error) {
      console.error('Error loading automated backup status:', error);
    }
  };

  const loadUserBackups = async () => {
    try {
      setAutomatedBackupsLoading(true);
      const response = await axios.get(`${API}/super-admin/backup/automated/users?session_id=${sessionId}`);
      setUserBackups(response.data.users || []);
    } catch (error) {
      console.error('Error loading user backups:', error);
      toast.error('Failed to load user backups');
    } finally {
      setAutomatedBackupsLoading(false);
    }
  };

  const handleTriggerAutomatedBackup = async () => {
    try {
      toast.loading('Triggering automated backups...');
      await axios.post(`${API}/super-admin/backup/automated/trigger?session_id=${sessionId}`);
      toast.success('Automated backups triggered successfully');
      loadAutomatedBackupStatus();
      loadUserBackups();
    } catch (error) {
      console.error('Error triggering automated backup:', error);
      toast.error('Failed to trigger automated backup');
    }
  };

  useEffect(() => {
    if (isLoggedIn && sessionId) {
      loadAnalytics(sessionId);
    }
  }, [isLoggedIn, sessionId]);

  useEffect(() => {
    if (isLoggedIn && sessionId && activeTab === 'users') {
      loadUsers();
    }
  }, [isLoggedIn, sessionId, activeTab, searchQuery, planFilter]);

  useEffect(() => {
    if (isLoggedIn && sessionId && activeTab === 'vip_backups') {
      loadBackups();
      loadLatestBackup();
    }
  }, [isLoggedIn, sessionId, activeTab]);

  useEffect(() => {
    if (isLoggedIn && sessionId && activeTab === 'automated_backups') {
      loadAutomatedBackupStatus();
      loadUserBackups();
    }
  }, [isLoggedIn, sessionId, activeTab]);

  // Login Page
  if (!isLoggedIn) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-900 via-purple-900 to-slate-900 flex items-center justify-center p-6">
        <Card className="w-full max-w-md shadow-2xl border-0 bg-white/10 backdrop-blur-md">
          <CardHeader className="text-center py-8 bg-gradient-to-r from-indigo-600 to-purple-600 text-white rounded-t-lg">
            <div className="flex justify-center mb-4">
              <div className="w-16 h-16 bg-white/20 rounded-full flex items-center justify-center">
                <Shield className="w-8 h-8 text-white" />
              </div>
            </div>
            <CardTitle className="text-2xl font-bold">Super Admin Access</CardTitle>
          </CardHeader>
          <CardContent className="p-8">
            <form onSubmit={handleLogin} className="space-y-6">
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Username
                </label>
                <Input
                  type="text"
                  value={loginForm.username}
                  onChange={(e) => setLoginForm({ ...loginForm, username: e.target.value })}
                  className="w-full bg-white/10 border-white/20 text-white placeholder-white/60"
                  placeholder="Enter username"
                  required
                />
              </div>
              <div>
                <label className="block text-sm font-medium text-white mb-2">
                  Password
                </label>
                <Input
                  type="password"
                  value={loginForm.password}
                  onChange={(e) => setLoginForm({ ...loginForm, password: e.target.value })}
                  className="w-full bg-white/10 border-white/20 text-white placeholder-white/60"
                  placeholder="Enter password"
                  required
                />
              </div>
              <Button
                type="submit"
                className="w-full bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white"
                disabled={isLoading}
              >
                {isLoading ? 'Authenticating...' : 'Access Dashboard'}
              </Button>
            </form>
          </CardContent>
        </Card>
        <Toaster position="top-center" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-indigo-50 p-6">
      <div className="max-w-7xl mx-auto">
        {/* Header */}
        <div className="flex items-center justify-between mb-8">
          <div className="flex items-center gap-4">
            <a href="/" className="cursor-pointer hover:opacity-80 transition-opacity">
              <img src="/tyme-logo-new.png" alt="TYME" className="h-12" />
            </a>
            <h1 className="text-3xl font-bold bg-gradient-to-r from-indigo-600 to-purple-600 bg-clip-text text-transparent">
              Super Admin Dashboard
            </h1>
          </div>
          <div className="flex items-center gap-4">
            <Badge className="bg-green-100 text-green-800">
              {lastUpdated ? `Updated ${lastUpdated.toLocaleTimeString()}` : 'Loading...'}
            </Badge>
            <Button 
              onClick={handleLogout}
              variant="outline"
              className="border-red-200 text-red-600 hover:bg-red-50"
            >
              Logout
            </Button>
          </div>
        </div>

        {/* Tabs Navigation */}
        <div className="mb-6 flex gap-4 border-b border-gray-200">
          <button
            onClick={() => setActiveTab('overview')}
            className={`px-6 py-3 text-sm font-medium transition-colors border-b-2 ${
              activeTab === 'overview'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-600 hover:text-indigo-600'
            }`}
          >
            Overview & Analytics
          </button>
          <button
            onClick={() => setActiveTab('users')}
            className={`px-6 py-3 text-sm font-medium transition-colors border-b-2 ${
              activeTab === 'users'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-600 hover:text-indigo-600'
            }`}
          >
            User Management
          </button>
          <button
            onClick={() => setActiveTab('vip_backups')}
            className={`px-6 py-3 text-sm font-medium transition-colors border-b-2 ${
              activeTab === 'vip_backups'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-600 hover:text-indigo-600'
            }`}
          >
            VIP Backups
          </button>
          <button
            onClick={() => setActiveTab('automated_backups')}
            className={`px-6 py-3 text-sm font-medium transition-colors border-b-2 ${
              activeTab === 'automated_backups'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-600 hover:text-indigo-600'
            }`}
          >
            Automated Backups
          </button>
          <button
            onClick={() => setActiveTab('ai_assistant')}
            className={`px-6 py-3 text-sm font-medium transition-colors border-b-2 ${
              activeTab === 'ai_assistant'
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-600 hover:text-indigo-600'
            }`}
          >
            🤖 AI Assistant
          </button>
        </div>

        {/* Overview Tab */}
        {activeTab === 'overview' && analytics && (
          <>
            {/* Overview Cards */}
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-6 mb-8">
              <Card className="bg-gradient-to-r from-blue-500 to-blue-600 text-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-blue-100 text-sm">Total Users</p>
                      <p className="text-3xl font-bold">{analytics.overview.total_users}</p>
                    </div>
                    <Users className="w-8 h-8 text-blue-200" />
                  </div>
                  <p className="text-blue-100 text-xs mt-2">
                    +{analytics.overview.recent_users} this week
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-r from-green-500 to-green-600 text-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-green-100 text-sm">Active Users</p>
                      <p className="text-3xl font-bold">{analytics.overview.active_users}</p>
                    </div>
                    <Activity className="w-8 h-8 text-green-200" />
                  </div>
                  <p className="text-green-100 text-xs mt-2">
                    {((analytics.overview.active_users / analytics.overview.total_users) * 100).toFixed(1)}% activation rate
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-r from-purple-500 to-purple-600 text-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-purple-100 text-sm">Locations</p>
                      <p className="text-3xl font-bold">{analytics.overview.total_locations}</p>
                    </div>
                    <MapPin className="w-8 h-8 text-purple-200" />
                  </div>
                  <p className="text-purple-100 text-xs mt-2">
                    {analytics.overview.total_employees} total employees
                  </p>
                </CardContent>
              </Card>

              <Card className="bg-gradient-to-r from-orange-500 to-orange-600 text-white">
                <CardContent className="p-6">
                  <div className="flex items-center justify-between">
                    <div>
                      <p className="text-orange-100 text-sm">Weekly Events</p>
                      <p className="text-3xl font-bold">{analytics.overview.recent_events}</p>
                    </div>
                    <Clock className="w-8 h-8 text-orange-200" />
                  </div>
                  <p className="text-orange-100 text-xs mt-2">
                    Check-ins/outs this week
                  </p>
                </CardContent>
              </Card>
            </div>

            {/* Analytics Grid */}
            <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 mb-8">
              {/* Plan Distribution */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <BarChart3 className="w-5 h-5" />
                    Plan Distribution
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {Object.entries(analytics.plan_distribution).map(([plan, count]) => (
                      <div key={plan} className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <Badge className={
                            plan === 'starter' ? 'bg-blue-100 text-blue-800' :
                            plan === 'pro' ? 'bg-purple-100 text-purple-800' :
                            'bg-gold-100 text-gold-800'
                          }>
                            {plan.charAt(0).toUpperCase() + plan.slice(1)}
                          </Badge>
                          <span className="text-slate-600">
                            {((count / analytics.overview.total_users) * 100).toFixed(1)}%
                          </span>
                        </div>
                        <span className="font-semibold">{count}</span>
                      </div>
                    ))}
                  </div>
                </CardContent>
              </Card>

              {/* Most Active Locations */}
              <Card>
                <CardHeader>
                  <CardTitle className="flex items-center gap-2">
                    <TrendingUp className="w-5 h-5" />
                    Most Active Locations
                  </CardTitle>
                </CardHeader>
                <CardContent>
                  <div className="space-y-4">
                    {analytics.active_locations.slice(0, 5).map((location, index) => (
                      <div key={index} className="flex items-center justify-between">
                        <div className="flex items-center gap-3">
                          <div className="w-8 h-8 bg-gradient-to-r from-indigo-100 to-purple-100 rounded-full flex items-center justify-center text-sm font-semibold text-indigo-600">
                            #{index + 1}
                          </div>
                          <span className="font-medium">{location.name}</span>
                        </div>
                        <Badge variant="outline">{location.event_count} events</Badge>
                      </div>
                    ))}
                    {analytics.active_locations.length === 0 && (
                      <p className="text-slate-500 text-center py-4">No recent activity</p>
                    )}
                  </div>
                </CardContent>
              </Card>
            </div>

            {/* Recent AI Queries */}
            <Card className="mb-8">
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <MessageCircle className="w-5 h-5" />
                  Recent AI Assistant Queries
                  <Badge className="bg-indigo-100 text-indigo-800 ml-2">
                    {analytics.overview.ai_queries_total} total
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="space-y-3">
                  {analytics.recent_ai_queries.slice(0, 8).map((query, index) => (
                    <div key={index} className="flex items-start gap-3 p-3 bg-slate-50 rounded-lg">
                      <Bot className="w-4 h-4 text-indigo-600 mt-1 flex-shrink-0" />
                      <div className="flex-1">
                        <p className="text-sm text-slate-800">{query.query}</p>
                        <p className="text-xs text-slate-500 mt-1">
                          {new Date(query.timestamp).toLocaleString()}
                        </p>
                      </div>
                    </div>
                  ))}
                  {analytics.recent_ai_queries.length === 0 && (
                    <p className="text-slate-500 text-center py-8">No recent AI queries</p>
                  )}
                </div>
              </CardContent>
            </Card>
          </>
        )}

        {/* User Management Tab */}
        {activeTab === 'users' && (
          <div className="space-y-6">
            {/* User Management Controls */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  User Management
                </CardTitle>
              </CardHeader>
              <CardContent>
                <div className="flex flex-col sm:flex-row gap-4 mb-6">
                  <Input
                    placeholder="Search users by name or email..."
                    value={searchQuery}
                    onChange={(e) => setSearchQuery(e.target.value)}
                    className="flex-1"
                  />
                  <select
                    value={planFilter}
                    onChange={(e) => setPlanFilter(e.target.value)}
                    className="px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="all">All Plans</option>
                    <option value="starter">Starter</option>
                    <option value="pro">Pro</option>
                    <option value="business">Business</option>
                  </select>
                </div>

                {/* Users List */}
                {usersLoading ? (
                  <TymeLoader size="md" text="Loading users..." />
                ) : (
                  <div className="space-y-4">
                    {users.map((user) => (
                      <div key={user.id} className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50">
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <h3 className="font-semibold text-gray-900">{user.name}</h3>
                              <Badge className={
                                user.plan === 'starter' ? 'bg-blue-100 text-blue-800' :
                                user.plan === 'pro' ? 'bg-purple-100 text-purple-800' :
                                'bg-gold-100 text-gold-800'
                              }>
                                {user.plan}
                              </Badge>
                              {user.is_vip && (
                                <Badge className="bg-yellow-100 text-yellow-800">VIP</Badge>
                              )}
                              <Badge className={
                                user.subscription_status === 'active' ? 'bg-green-100 text-green-800' :
                                user.subscription_status === 'suspended' ? 'bg-red-100 text-red-800' :
                                'bg-gray-100 text-gray-800'
                              }>
                                {user.subscription_status || 'unknown'}
                              </Badge>
                            </div>
                            <p className="text-sm text-gray-600">{user.email}</p>
                            <div className="flex items-center gap-4 mt-2 text-xs text-gray-500">
                              <span>Joined: {new Date(user.created_at).toLocaleDateString()}</span>
                              <span>Locations: {user.location_count || 0}</span>
                              <span>Employees: {user.employee_count || 0}</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              onClick={() => {
                                setSelectedUser(user);
                                setPlanChangeData({ plan: user.plan, isVip: user.is_vip, reason: '' });
                                setShowPlanChangeModal(true);
                              }}
                              variant="outline"
                              size="sm"
                              className="text-indigo-600 border-indigo-200 hover:bg-indigo-50"
                            >
                              <Eye className="w-4 h-4 mr-1" />
                              Manage
                            </Button>
                            {user.subscription_status === 'active' && (
                              <Button
                                onClick={() => handleSuspendUser(user.id)}
                                variant="outline"
                                size="sm"
                                className="text-orange-600 border-orange-200 hover:bg-orange-50"
                              >
                                Suspend
                              </Button>
                            )}
                            <Button
                              onClick={() => handleDeleteUser(user.id)}
                              variant="outline"
                              size="sm"
                              className="text-red-600 border-red-200 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                    {users.length === 0 && (
                      <div className="text-center py-8">
                        <Users className="w-12 h-12 mx-auto text-gray-300 mb-2" />
                        <p className="text-gray-500">No users found</p>
                      </div>
                    )}
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Plan Change Modal */}
        {showPlanChangeModal && selectedUser && (
          <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50">
            <Card className="w-full max-w-md mx-4">
              <CardHeader>
                <CardTitle>Manage User: {selectedUser.name}</CardTitle>
              </CardHeader>
              <CardContent className="space-y-4">
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Plan
                  </label>
                  <select
                    value={planChangeData.plan}
                    onChange={(e) => setPlanChangeData({ ...planChangeData, plan: e.target.value })}
                    className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-indigo-500"
                  >
                    <option value="starter">Starter</option>
                    <option value="pro">Pro</option>
                    <option value="business">Business</option>
                  </select>
                </div>
                <div className="flex items-center">
                  <input
                    type="checkbox"
                    id="vip-status"
                    checked={planChangeData.isVip}
                    onChange={(e) => setPlanChangeData({ ...planChangeData, isVip: e.target.checked })}
                    className="mr-2"
                  />
                  <label htmlFor="vip-status" className="text-sm font-medium text-gray-700">
                    VIP Status
                  </label>
                </div>
                <div>
                  <label className="block text-sm font-medium text-gray-700 mb-2">
                    Reason (Optional)
                  </label>
                  <Input
                    value={planChangeData.reason}
                    onChange={(e) => setPlanChangeData({ ...planChangeData, reason: e.target.value })}
                    placeholder="Reason for change..."
                  />
                </div>
                <div className="flex gap-2 pt-4">
                  <Button
                    onClick={handlePlanChange}
                    className="flex-1 bg-indigo-600 hover:bg-indigo-700 text-white"
                  >
                    Update Plan
                  </Button>
                  <Button
                    onClick={() => {
                      setShowPlanChangeModal(false);
                      setSelectedUser(null);
                      setPlanChangeData({ plan: '', isVip: false, reason: '' });
                    }}
                    variant="outline"
                    className="flex-1"
                  >
                    Cancel
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* VIP Backups Tab */}
        {activeTab === 'vip_backups' && (
          <div className="space-y-6">
            {/* VIP Backups Header */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Shield className="w-5 h-5" />
                      Sibel VIP Data Backups
                    </CardTitle>
                    <p className="text-sm text-gray-500 mt-1">
                      Automated real-time backups with manual restore capability
                    </p>
                  </div>
                  <Button
                    onClick={handleCreateBackup}
                    className="bg-gradient-to-r from-green-600 to-green-700 hover:from-green-700 hover:to-green-800 text-white"
                  >
                    <Plus className="w-4 h-4 mr-2" />
                    Create Manual Backup
                  </Button>
                </div>
              </CardHeader>
              {latestBackup && (
                <CardContent>
                  <div className="bg-gradient-to-r from-blue-50 to-indigo-50 border border-indigo-200 rounded-lg p-4">
                    <div className="flex items-center justify-between">
                      <div>
                        <p className="text-sm font-medium text-indigo-900">Latest Backup</p>
                        <p className="text-xs text-indigo-600 mt-1">
                          {new Date(latestBackup.timestamp).toLocaleString()}
                        </p>
                      </div>
                      <div className="text-right">
                        <p className="text-xs text-indigo-700">
                          {latestBackup.stats.locations_count} Locations • {latestBackup.stats.employees_count} Employees
                        </p>
                        <p className="text-xs text-indigo-700">
                          {latestBackup.stats.events_count} Events
                        </p>
                      </div>
                    </div>
                  </div>
                </CardContent>
              )}
            </Card>

            {/* Backups List */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <History className="w-5 h-5" />
                  Backup History
                  <Badge className="bg-indigo-100 text-indigo-800 ml-2">
                    {backups.length} snapshots
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {backupsLoading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
                    <p className="text-slate-600 mt-2">Loading backups...</p>
                  </div>
                ) : backups.length > 0 ? (
                  <div className="space-y-3">
                    {backups.map((backup) => (
                      <div
                        key={backup.id}
                        className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <Badge className="bg-purple-100 text-purple-800">
                                {backup.vip_user}
                              </Badge>
                              <p className="text-sm font-medium text-gray-900">
                                {new Date(backup.timestamp).toLocaleString()}
                              </p>
                            </div>
                            <div className="flex items-center gap-4 text-xs text-gray-600">
                              <span>📍 {backup.stats.locations_count} Locations</span>
                              <span>👥 {backup.stats.employees_count} Employees</span>
                              <span>📊 {backup.stats.events_count} Events</span>
                              <span>⚙️ {backup.stats.customizations_count} Customizations</span>
                            </div>
                          </div>
                          <div className="flex items-center gap-2">
                            <Button
                              onClick={() => handleRestoreBackup(backup.id)}
                              variant="outline"
                              size="sm"
                              className="text-blue-600 border-blue-200 hover:bg-blue-50"
                            >
                              <Zap className="w-4 h-4 mr-1" />
                              Restore
                            </Button>
                            <Button
                              onClick={() => handleDeleteBackup(backup.id)}
                              variant="outline"
                              size="sm"
                              className="text-red-600 border-red-200 hover:bg-red-50"
                            >
                              <Trash2 className="w-4 h-4" />
                            </Button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Shield className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500 text-lg font-medium">No backups yet</p>
                    <p className="text-gray-400 text-sm mt-2">
                      Backups are created automatically when Sibel performs data operations
                    </p>
                    <Button
                      onClick={handleCreateBackup}
                      className="mt-4 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white"
                    >
                      Create First Backup
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* Automated Backups Tab */}
        {activeTab === 'automated_backups' && (
          <div className="space-y-6">
            {/* Backup Status Card */}
            <Card>
              <CardHeader>
                <div className="flex items-center justify-between">
                  <div>
                    <CardTitle className="flex items-center gap-2">
                      <Clock className="w-5 h-5" />
                      Automated Backup System
                    </CardTitle>
                    <p className="text-sm text-gray-500 mt-1">
                      All users automatically backed up every 3 hours • Last 2 backups kept per user
                    </p>
                  </div>
                  <Button
                    onClick={handleTriggerAutomatedBackup}
                    className="bg-gradient-to-r from-blue-600 to-indigo-700 hover:from-blue-700 hover:to-indigo-800 text-white"
                  >
                    <Zap className="w-4 h-4 mr-2" />
                    Trigger Now
                  </Button>
                </div>
              </CardHeader>
              {automatedBackupStatus && (
                <CardContent>
                  <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                    <div className="bg-gradient-to-r from-green-50 to-green-100 border border-green-200 rounded-lg p-4">
                      <p className="text-xs text-green-700 font-medium">Status</p>
                      <p className="text-2xl font-bold text-green-900 mt-1">
                        {automatedBackupStatus.status === 'active' ? '✓ Active' : '⚠ Inactive'}
                      </p>
                    </div>
                    <div className="bg-gradient-to-r from-blue-50 to-blue-100 border border-blue-200 rounded-lg p-4">
                      <p className="text-xs text-blue-700 font-medium">Next Backup</p>
                      <p className="text-sm font-bold text-blue-900 mt-1">
                        {automatedBackupStatus.next_run ? new Date(automatedBackupStatus.next_run).toLocaleString() : 'N/A'}
                      </p>
                    </div>
                    <div className="bg-gradient-to-r from-purple-50 to-purple-100 border border-purple-200 rounded-lg p-4">
                      <p className="text-xs text-purple-700 font-medium">Total Backups</p>
                      <p className="text-2xl font-bold text-purple-900 mt-1">
                        {automatedBackupStatus.total_backups || 0}
                      </p>
                    </div>
                    <div className="bg-gradient-to-r from-orange-50 to-orange-100 border border-orange-200 rounded-lg p-4">
                      <p className="text-xs text-orange-700 font-medium">Users Backed Up</p>
                      <p className="text-2xl font-bold text-orange-900 mt-1">
                        {automatedBackupStatus.users_backed_up || 0}
                      </p>
                    </div>
                  </div>
                  {automatedBackupStatus.last_backup_time && (
                    <div className="mt-4 text-sm text-gray-600">
                      Last automated backup: {new Date(automatedBackupStatus.last_backup_time).toLocaleString()}
                    </div>
                  )}
                </CardContent>
              )}
            </Card>

            {/* User Backups List */}
            <Card>
              <CardHeader>
                <CardTitle className="flex items-center gap-2">
                  <Users className="w-5 h-5" />
                  User Backup Status
                  <Badge className="bg-indigo-100 text-indigo-800 ml-2">
                    {userBackups.length} users
                  </Badge>
                </CardTitle>
              </CardHeader>
              <CardContent>
                {automatedBackupsLoading ? (
                  <div className="text-center py-8">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-indigo-600 mx-auto"></div>
                    <p className="text-slate-600 mt-2">Loading user backups...</p>
                  </div>
                ) : userBackups.length > 0 ? (
                  <div className="space-y-3">
                    {userBackups.map((user) => (
                      <div
                        key={user.user_email}
                        className="border border-gray-200 rounded-lg p-4 hover:bg-gray-50 transition-colors"
                      >
                        <div className="flex items-center justify-between">
                          <div className="flex-1">
                            <div className="flex items-center gap-3 mb-2">
                              <p className="font-medium text-gray-900">{user.user_name}</p>
                              <Badge className="bg-blue-100 text-blue-800">
                                {user.backup_count} backups
                              </Badge>
                            </div>
                            <p className="text-sm text-gray-600">{user.user_email}</p>
                            {user.latest_backup && (
                              <div className="mt-2 text-xs text-gray-500">
                                Latest backup: {new Date(user.latest_backup.timestamp).toLocaleString()}
                              </div>
                            )}
                            {user.latest_backup?.stats && (
                              <div className="flex items-center gap-4 mt-2 text-xs text-gray-600">
                                <span>📍 {user.latest_backup.stats.locations_count} Locations</span>
                                <span>👥 {user.latest_backup.stats.employees_count} Employees</span>
                                <span>📊 {user.latest_backup.stats.events_count} Events</span>
                              </div>
                            )}
                          </div>
                          <Badge 
                            className={
                              user.backup_count >= 2 
                                ? "bg-green-100 text-green-800" 
                                : "bg-yellow-100 text-yellow-800"
                            }
                          >
                            {user.backup_count >= 2 ? 'Healthy' : 'Partial'}
                          </Badge>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="text-center py-12">
                    <Clock className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                    <p className="text-gray-500 text-lg font-medium">No automated backups yet</p>
                    <p className="text-gray-400 text-sm mt-2">
                      Backups will be created automatically every 3 hours
                    </p>
                    <Button
                      onClick={handleTriggerAutomatedBackup}
                      className="mt-4 bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white"
                    >
                      Trigger First Backup
                    </Button>
                  </div>
                )}
              </CardContent>
            </Card>
          </div>
        )}

        {/* AI Admin Assistant Tab */}
        {activeTab === 'ai_assistant' && (
          <div className="space-y-6">
            <Card>
              <CardHeader className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white">
                <CardTitle className="flex items-center gap-3">
                  <Bot className="w-6 h-6" />
                  AI Admin Assistant - File-Powered Management
                </CardTitle>
                <p className="text-sm opacity-90 mt-2">
                  Upload CSV files, analyze data, restore user information, and manage the platform with AI assistance
                </p>
              </CardHeader>
              <CardContent className="p-6">
                {/* Chat Messages */}
                <div className="bg-gray-50 rounded-lg p-4 mb-4 h-[500px] overflow-y-auto">
                  {aiMessages.length === 0 ? (
                    <div className="text-center py-12">
                      <Bot className="w-16 h-16 mx-auto text-gray-300 mb-4" />
                      <h3 className="text-lg font-semibold text-gray-700 mb-2">
                        Welcome to AI Admin Assistant
                      </h3>
                      <p className="text-gray-600 text-sm max-w-md mx-auto">
                        I can help you with:
                      </p>
                      <ul className="text-left text-sm text-gray-600 mt-4 max-w-md mx-auto space-y-2">
                        <li>📊 Analyzing CSV files with user/attendance data</li>
                        <li>♻️ Restoring user data from backup files</li>
                        <li>👥 Managing user accounts and permissions</li>
                        <li>🔍 Reviewing platform statistics and insights</li>
                        <li>📝 Suggesting data restoration steps</li>
                      </ul>
                      <p className="text-xs text-gray-500 mt-4">
                        Upload a CSV file or ask me a question to get started!
                      </p>
                    </div>
                  ) : (
                    <div className="space-y-4">
                      {aiMessages.map((msg, idx) => (
                        <div key={idx} className={`flex ${msg.type === 'user' ? 'justify-end' : 'justify-start'}`}>
                          <div className={`max-w-[80%] rounded-lg p-3 ${
                            msg.type === 'user' 
                              ? 'bg-indigo-600 text-white' 
                              : 'bg-white border border-gray-200'
                          }`}>
                            {msg.files && msg.files.length > 0 && (
                              <div className="mb-2 pb-2 border-b border-gray-200">
                                <p className="text-xs font-semibold mb-1">📎 Attached Files:</p>
                                {msg.files.map((file, fidx) => (
                                  <Badge key={fidx} variant="outline" className="mr-1 text-xs">
                                    {file}
                                  </Badge>
                                ))}
                              </div>
                            )}
                            <p className="text-sm whitespace-pre-wrap">{msg.content}</p>
                            <p className="text-xs opacity-70 mt-1">{new Date(msg.timestamp).toLocaleTimeString()}</p>
                          </div>
                        </div>
                      ))}
                      {aiLoading && (
                        <div className="flex justify-start">
                          <div className="bg-white border border-gray-200 rounded-lg p-3 max-w-[80%]">
                            <div className="flex items-center gap-2">
                              <div className="animate-spin rounded-full h-4 w-4 border-2 border-indigo-600 border-t-transparent"></div>
                              <span className="text-sm text-gray-600">AI is thinking...</span>
                            </div>
                          </div>
                        </div>
                      )}
                      <div ref={aiMessagesEndRef} />
                    </div>
                  )}
                </div>

                {/* File Upload Section */}
                {selectedFiles.length > 0 && (
                  <div className="mb-4 p-3 bg-blue-50 rounded-lg border border-blue-200">
                    <div className="flex items-center justify-between mb-2">
                      <span className="text-sm font-semibold text-blue-900">Selected Files:</span>
                      <Button
                        size="sm"
                        variant="outline"
                        onClick={() => setSelectedFiles([])}
                        className="text-xs"
                      >
                        Clear All
                      </Button>
                    </div>
                    <div className="space-y-1">
                      {selectedFiles.map((file, idx) => (
                        <div key={idx} className="flex items-center justify-between bg-white p-2 rounded">
                          <span className="text-sm text-gray-700">📄 {file.name}</span>
                          <Button
                            size="sm"
                            variant="ghost"
                            onClick={() => setSelectedFiles(selectedFiles.filter((_, i) => i !== idx))}
                          >
                            <X className="w-4 h-4" />
                          </Button>
                        </div>
                      ))}
                    </div>
                  </div>
                )}

                {/* Input Section */}
                <div className="flex gap-2">
                  <label className="cursor-pointer">
                    <input
                      type="file"
                      multiple
                      accept=".csv,.pdf,.json,.txt"
                      onChange={(e) => {
                        const files = Array.from(e.target.files || []);
                        setSelectedFiles(prev => [...prev, ...files]);
                        e.target.value = '';
                      }}
                      className="hidden"
                    />
                    <Button
                      type="button"
                      variant="outline"
                      className="border-indigo-200 text-indigo-700 hover:bg-indigo-50"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      Upload Files
                    </Button>
                  </label>
                  
                  <Input
                    value={aiInput}
                    onChange={(e) => setAiInput(e.target.value)}
                    onKeyPress={(e) => e.key === 'Enter' && !e.shiftKey && handleAiSendMessage()}
                    placeholder="Ask the AI assistant or describe what you need help with..."
                    className="flex-1"
                    disabled={aiLoading}
                  />
                  
                  <Button
                    onClick={handleAiSendMessage}
                    disabled={aiLoading || (!aiInput.trim() && selectedFiles.length === 0)}
                    className="bg-gradient-to-r from-indigo-600 to-purple-600 hover:from-indigo-700 hover:to-purple-700 text-white"
                  >
                    <Send className="w-4 h-4" />
                  </Button>
                </div>

                {/* Clear Chat Button */}
                <div className="mt-4 flex justify-end">
                  <Button
                    onClick={handleClearAiChat}
                    variant="outline"
                    size="sm"
                    className="text-gray-600"
                    disabled={aiMessages.length === 0}
                  >
                    <Trash2 className="w-4 h-4 mr-2" />
                    Clear Chat History
                  </Button>
                </div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Jarvis AI Assistant - Floating Button */}
        {!showJarvis && (
          <div className="fixed bottom-6 right-6 z-50">
            <Button
              onClick={() => {
                setShowJarvis(true);
                if (chatHistory.length === 0) {
                  createNewChat();
                } else if (!currentChatId) {
                  loadChat(chatHistory[0].id);
                }
              }}
              className="w-16 h-16 rounded-full bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white shadow-lg hover:shadow-xl transition-all duration-200"
              aria-label="Open Jarvis"
            >
              <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-white/30">
                <img 
                  src="/jarvis-avatar.png" 
                  alt="Jarvis AI" 
                  className="w-full h-full object-cover"
                />
              </div>
            </Button>
          </div>
        )}

        {/* Jarvis AI Assistant - Chat Window */}
        {showJarvis && (
          <div className="fixed bottom-6 right-6 z-50 flex gap-4">
            {/* Chat History Sidebar */}
            {showChatHistory && (
              <Card className="w-80 h-[600px] shadow-2xl border-2 border-purple-200">
                <CardHeader className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-4 rounded-t-lg">
                  <div className="flex items-center justify-between">
                    <CardTitle className="text-lg flex items-center gap-2">
                      <History className="w-5 h-5" />
                      Chat History
                    </CardTitle>
                    <Button
                      onClick={() => setShowChatHistory(false)}
                      variant="ghost"
                      size="sm"
                      className="text-white hover:bg-white/20 p-1 h-8 w-8"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </CardHeader>
                <CardContent className="p-0 h-[calc(100%-80px)] flex flex-col">
                  <div className="p-4 border-b">
                    <Button
                      onClick={createNewChat}
                      className="w-full bg-gradient-to-r from-purple-600 to-indigo-600 text-white"
                      size="sm"
                    >
                      <Plus className="w-4 h-4 mr-2" />
                      New Chat
                    </Button>
                  </div>
                  <div className="flex-1 overflow-y-auto">
                    {chatHistory.map((chat) => (
                      <div
                        key={chat.id}
                        className={`flex items-center justify-between p-3 border-b hover:bg-slate-50 cursor-pointer ${
                          currentChatId === chat.id ? 'bg-purple-50 border-purple-200' : ''
                        }`}
                        onClick={() => loadChat(chat.id)}
                      >
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium text-slate-900 truncate">
                            {chat.title}
                          </p>
                          <p className="text-xs text-slate-500">
                            {new Date(chat.lastActivity).toLocaleDateString()}
                          </p>
                        </div>
                        <Button
                          onClick={(e) => {
                            e.stopPropagation();
                            deleteChat(chat.id);
                          }}
                          variant="ghost"
                          size="sm"
                          className="p-1 h-6 w-6 text-slate-400 hover:text-red-600"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    ))}
                    {chatHistory.length === 0 && (
                      <div className="p-6 text-center text-slate-500">
                        <MessageCircle className="w-12 h-12 mx-auto mb-2 text-slate-300" />
                        <p className="text-sm">No chat history yet</p>
                      </div>
                    )}
                  </div>
                </CardContent>
              </Card>
            )}

            {/* Main Chat Window */}
            <Card className="w-96 h-[600px] shadow-2xl border-2 border-purple-200">
              <CardHeader className="bg-gradient-to-r from-purple-600 to-indigo-600 text-white p-4 rounded-t-lg flex-shrink-0">
                <div className="flex items-center justify-between">
                  <CardTitle className="text-lg flex items-center gap-3">
                    <div className="w-10 h-10 rounded-full overflow-hidden border-2 border-white/30">
                      <img 
                        src="/jarvis-avatar.png" 
                        alt="Jarvis AI" 
                        className="w-full h-full object-cover"
                      />
                    </div>
                    Jarvis AI Analytics
                  </CardTitle>
                  <div className="flex items-center gap-2">
                    <Button
                      onClick={() => setShowChatHistory(!showChatHistory)}
                      variant="ghost"
                      size="sm"
                      className="text-white hover:bg-white/20 p-1 h-8 w-8"
                      title="Chat History"
                    >
                      <History className="w-4 h-4" />
                    </Button>
                    <Button
                      onClick={() => setShowJarvis(false)}
                      variant="ghost"
                      size="sm"
                      className="text-white hover:bg-white/20 p-1 h-8 w-8"
                    >
                      <X className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardHeader>
              
              <CardContent className="flex-1 flex flex-col p-0 min-h-0">
                {/* Messages */}
                <div className="flex-1 overflow-y-auto p-4 space-y-3">
                  {jarvisMessages.map((message, index) => (
                    <div
                      key={index}
                      className={`flex items-start gap-2 ${
                        message.type === 'user' ? 'justify-end' : 'justify-start'
                      }`}
                    >
                      {message.type === 'bot' && (
                        <div className="flex-shrink-0 w-8 h-8 rounded-full overflow-hidden border-2 border-purple-200">
                          <img 
                            src="/jarvis-avatar.png" 
                            alt="Jarvis" 
                            className="w-full h-full object-cover"
                          />
                        </div>
                      )}
                      
                      <div
                        className={`max-w-[80%] p-3 rounded-lg text-sm ${
                          message.type === 'user'
                            ? 'bg-gradient-to-r from-purple-600 to-indigo-600 text-white'
                            : 'bg-slate-100 text-slate-800'
                        }`}
                      >
                        {message.content}
                      </div>
                      
                      {message.type === 'user' && (
                        <div className="flex-shrink-0 w-8 h-8 bg-slate-600 rounded-full flex items-center justify-center">
                          <User className="w-4 h-4 text-white" />
                        </div>
                      )}
                    </div>
                  ))}
                  
                  {jarvisLoading && (
                    <div className="flex items-start gap-2">
                      <div className="flex-shrink-0 w-8 h-8 rounded-full overflow-hidden border-2 border-purple-200">
                        <img 
                          src="/jarvis-avatar.png" 
                          alt="Jarvis" 
                          className="w-full h-full object-cover"
                        />
                      </div>
                      <div className="bg-slate-100 p-3 rounded-lg text-sm text-slate-600">
                        <div className="flex space-x-1">
                          <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce"></div>
                          <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0.1s' }}></div>
                          <div className="w-2 h-2 bg-slate-400 rounded-full animate-bounce" style={{ animationDelay: '0.2s' }}></div>
                        </div>
                      </div>
                    </div>
                  )}
                  
                  <div ref={messagesEndRef} />
                </div>

                {/* Input */}
                <div className="border-t p-4 flex-shrink-0 bg-white">
                  <div className="flex gap-2">
                    <Input
                      value={jarvisInput}
                      onChange={(e) => setJarvisInput(e.target.value)}
                      onKeyPress={handleJarvisKeyPress}
                      placeholder="Ask Jarvis about analytics..."
                      className="flex-1"
                      disabled={jarvisLoading}
                    />
                    <Button
                      onClick={sendJarvisMessage}
                      disabled={!jarvisInput.trim() || jarvisLoading}
                      className="bg-gradient-to-r from-purple-600 to-indigo-600 hover:from-purple-700 hover:to-indigo-700 text-white flex-shrink-0"
                    >
                      <Send className="w-4 h-4" />
                    </Button>
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
      <Toaster position="top-center" />
    </div>
  );
};

export default SuperAdminDashboard;